import { useMemo } from 'react';

import { addNarrativeRequest } from 'utilitys/narrative/narrativeTools';

import { useGlobalFiltersHook } from 'hooks/useGlobalFiltersHook';

import { useResourceAnalysisStore } from 'store/resourceAnalysis.store';

import { useMutationExtractResourceChartBarToCsv } from 'api/mutations/extractResourceChartBarToCsv.mutation';
import { useQueryGetProjectDatesForResourceChart } from 'api/queries/getProjectDatesForResourceChart.query';
import { useQueryGetResourceChart } from 'api/queries/getResourceChart.query';

import { getInputLabel } from 'components/common/CustomFilters/TableDateRangeFilter/TableDateRangeFilter.utils';
import { FilterNames, getFiltersData } from 'components/common/GlobalFilterComponent/GlobalFilterComponent.utils';
import { rangeOptions } from 'components/common/GlobalFilterComponent/components/RangeFilter/rangeFilter';

import { usePinboardWidgetWrapperContext } from 'components/Dashboards/Project/Components/CustomDashboard/PinboardWidgetWrapper/context/widgetWrapperContext';
import { aggregationOptions } from 'components/widgets/ResourcesAnalysisChart/ResourcesAnalysisChart.consts';

export type UseResourcesAnalysisChartProvider = ReturnType<typeof useResourcesAnalysisChartProvider>;

interface Props {
    componentKey: string;
    widgetId?: string;
    latestVersionId?: number | undefined;
    compareVersionId?: number | undefined;
    globalFilterData: any;
    localFilters: any;
}

export const useResourcesAnalysisChartProvider = ({
    widgetId,
    latestVersionId,
    localFilters,
    globalFilterData,
    compareVersionId,
    componentKey,
}: Props) => {
    const { isGenerativeAi } = usePinboardWidgetWrapperContext();
    const isInPinboard = Boolean(widgetId);

    const getProjectDatesForResourceChartPayload = {
        lastMetaDataId: latestVersionId,
        comparedMetaDataId: compareVersionId,
    };

    const getProjectDatesForResourceChart = useQueryGetProjectDatesForResourceChart(
        getProjectDatesForResourceChartPayload,
    );

    const { update: updateResourcesAnalysisStore, aggregation } = useResourceAnalysisStore();

    const source = isInPinboard ? localFilters : { ...globalFilterData, aggregation };

    const { startDate, endDate } = useMemo(() => {
        const value = source?.dateRangeFilter;
        if (value && getProjectDatesForResourceChart.data) {
            if (Array.isArray(value)) {
                if (value[0] && value[1]) {
                    return {
                        startDate: value[0],
                        endDate: value[1],
                    };
                } else {
                    return {
                        startDate: getProjectDatesForResourceChart.data?.startProjectDate,
                        endDate: getProjectDatesForResourceChart.data?.endProjectDate,
                    };
                }
            }
        }
        return {
            startDate: null,
            endDate: null,
        };
    }, [source.dateRangeFilter, getProjectDatesForResourceChart.data]);

    const filters = useMemo(() => {
        return getFiltersData({
            [FilterNames.ACTIVITY_CODES]: source?.activityCodes
                ?.filter((item) => !item.value.isExclude)
                .map((i) => i.value.name),

            [FilterNames.ACTIVITY_CODES_EXCLUDE]: source?.activityCodes
                ?.filter((item) => item.value.isExclude)
                .map((i) => i.value.name),

            [FilterNames.FRAGNET_SEARCH]: source?.milestoneFragnet,

            [FilterNames.TAG_CARD_BOARD]: source?.tags
                ?.filter((item) => !item.value.isExclude)
                .map((i) => i.value.name),

            [FilterNames.TAG_CARD_BOARD_EXCLUDE]: source?.tags
                ?.filter((item) => item.value.isExclude)
                .map((i) => i.value.name),

            [FilterNames.WBS_SEARCH]: source?.wbs,
            [FilterNames.RESOURCE_NAME_CARD_BOARD]: source?.resource,
            [FilterNames.RESOURCE_TYPE_CARD_BOARD]: source?.resourceType,
        });
    }, [
        source?.activityCodes,
        source?.milestoneFragnet,
        source?.tags,
        source?.wbs,
        source?.activityType,
        source?.resource,
        source?.resourceType,
    ]);

    const range = useMemo(() => {
        const value = source.dateRangeFilter;
        return Array.isArray(value) ? 'R_CUSTOM' : value;
    }, [source.dateRangeFilter]);

    const getResourceChart = useQueryGetResourceChart({
        metaDataId: latestVersionId,
        comparedMetaDataId: isInPinboard ? latestVersionId : compareVersionId,
        range,
        startDate,
        endDate,
        filter: {
            filters: filters,
        },
        aggregation: source?.aggregation?.value,
        quickAnswerFilter: source?.quickAnswerFilter ? source?.quickAnswerFilter : undefined,
        ...addNarrativeRequest(isGenerativeAi, componentKey, widgetId),
    });

    const rangeFilterValue = useMemo(() => {
        if (!range) return null;
        if (range === 'R_CUSTOM') {
            return getInputLabel({ startDate: startDate, endDate: endDate });
        }
        return rangeOptions.find((item) => item.value === range)?.label;
    }, [range, startDate, endDate]);

    // create pill data for PinboardWidgetPills component
    const pillsData = {
        activityCodes: source?.activityCodes?.filter((item) => !item.value?.isExclude).map((i) => i.value?.name),
        activityCodesExclude: source?.activityCodes?.filter((item) => item.value?.isExclude).map((i) => i.value?.name),
        milestoneFragnet: source?.milestoneFragnet,
        wbs: source?.wbs,
        resourceType: source?.resourceType,
        dateRangeFilter: rangeFilterValue,
        quickAnswerFilter: source?.quickAnswerFilter,
        aggregation: aggregationOptions.find((item) => item.value === source?.aggregation?.value)?.label,
        resourceName: source?.resourceName,
        tags: source?.tags?.filter((item) => !item.value?.isExclude).map((i) => i.value?.name),
        tagsExclude: source?.tags?.filter((item) => item.value?.isExclude).map((i) => i.value?.name),
        resource: source?.resource,
    };

    const { pillsList: pills } = useGlobalFiltersHook({ widgetId, source: pillsData, latestVersionId });

    return {
        ui: {
            state: {
                dateRangeFilter: source?.dateRangeFilter,
                resourceType: source?.resourceType,
                activityCodes: source?.activityCodes,
                milestoneFragnet: source?.milestoneFragnet,
                tags: source?.tags,
                wbs: source?.wbs,
                quickAnswerFilter: source?.quickAnswerFilter,
                aggregation: source.aggregation,
                resource: source?.resource,
            },
            setters: {
                updateResourcesAnalysisStore,
            },
            pills,
            isInPinboard,
        },
        api: {
            exportToCsvResourceChartBar: {
                instance: useMutationExtractResourceChartBarToCsv(),
                payload: {
                    metaDataId: latestVersionId,
                    comparedMetaDataId: isInPinboard ? latestVersionId : compareVersionId,
                    range,
                    startDate,
                    endDate,
                    filter: {
                        filters: filters,
                    },
                    aggregation: source?.aggregation?.value,
                    quickAnswerFilter: source?.quickAnswerFilter,
                },
            },
            getResourceChart: {
                instance: getResourceChart,
                payload: getProjectDatesForResourceChartPayload,
            },
        },
    };
};
